import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
  type BufferGeometry,
  type Scene,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { mulberry32, lerp, smoothstep } from '../utils/rng';
import {
  FIRING_LINE_Z,
  PLAY_BOUNDS,
  terrainHeight,
  terrainNormal,
  terrainSlope,
} from './heightfield';
import { inTargetClearance } from './rangeLayout';

/**
 * Le decor du champ de tir : rochers, broussailles, blocs de beton, futs et
 * bornes de distance. Chaque famille tient dans un InstancedMesh, soit un
 * draw call pour des centaines d'objets.
 * cf. doc/06-boucle-de-jeu-et-perf.md
 */

const ROCK_COUNT = 300;
const SHRUB_COUNT = 520;
const BLOCK_COUNT = 46;
const DRUM_COUNT = 52;

/** Au-dela de cette pente, rien ne tient : on laisse la roche nue. */
const MAX_SLOPE = 0.44;
/** Rien ne pousse sur les sommets. */
const TREE_LINE = 84;
/** Degagement autour de la plateforme de tir. */
const PAD_RADIUS = 30;

interface Spot {
  x: number;
  z: number;
  y: number;
}

/**
 * Tirage par rejet : on echantillonne au hasard puis on ecarte les pentes
 * fortes, l'altitude, la plateforme de tir et le champ des cibles.
 */
function scatter(
  count: number,
  rng: () => number,
  opts: { maxHeight?: number; maxSlope?: number; floorBias?: number } = {},
): Spot[] {
  const out: Spot[] = [];
  const maxHeight = opts.maxHeight ?? TREE_LINE;
  const maxSlope = opts.maxSlope ?? MAX_SLOPE;
  let guard = count * 40;

  while (out.length < count && guard-- > 0) {
    const x = lerp(PLAY_BOUNDS.minX - 24, PLAY_BOUNDS.maxX + 24, rng());
    const z = lerp(PLAY_BOUNDS.minZ - 24, PLAY_BOUNDS.maxZ + 24, rng());
    const y = terrainHeight(x, z);
    if (y > maxHeight) continue;
    if (terrainSlope(x, z) > maxSlope) continue;
    if (x * x + (z - FIRING_LINE_Z) ** 2 < PAD_RADIUS * PAD_RADIUS) continue;
    if (inTargetClearance(x, z)) continue;
    // Densite plus forte sur le plat que sur les flancs des collines.
    if (opts.floorBias !== undefined) {
      const keep = lerp(1, 0.2, smoothstep(10, maxHeight, y));
      if (rng() > keep * opts.floorBias + (1 - opts.floorBias)) continue;
    }
    out.push({ x, y, z });
  }
  return out;
}

/** Touffe d'herbe sèche : trois lames croisees, aplaties. */
function shrubGeometry(): BufferGeometry {
  const blades = [0, 1.05, 2.1].map((a, i) =>
    new IcosahedronGeometry(1, 0)
      .scale(1, 0.52, 0.28)
      .rotateY(a)
      .translate((i - 1) * 0.22, 0.42, 0),
  );
  return mergeGeometries(blades, false)!;
}

/** Fut de 200 litres : corps, plus les deux cercles de roulement. */
function drumGeometry(): BufferGeometry {
  const parts = [
    new CylinderGeometry(0.29, 0.29, 0.88, 12, 1).translate(0, 0.44, 0),
    new CylinderGeometry(0.31, 0.31, 0.06, 12, 1).translate(0, 0.3, 0),
    new CylinderGeometry(0.31, 0.31, 0.06, 12, 1).translate(0, 0.58, 0),
  ];
  return mergeGeometries(parts, false)!;
}

function place(
  scene: Scene,
  spots: Spot[],
  geometry: BufferGeometry,
  material: MeshStandardMaterial,
  rng: () => number,
  opts: {
    scale: [number, number];
    /** Aplatissement vertical tire au hasard, pour casser la repetition. */
    squash?: [number, number];
    /** Suivre la normale du sol plutot que rester vertical. */
    alignToSlope?: boolean;
    /** Enfoncement dans le sol, en fraction de l'echelle. */
    sink?: number;
    tint?: number;
    tintSpread?: number;
  },
): InstancedMesh {
  const mesh = new InstancedMesh(geometry, material, spots.length);
  mesh.castShadow = true;
  mesh.receiveShadow = true;

  const m = new Matrix4();
  const q = new Quaternion();
  const spin = new Quaternion();
  const pos = new Vector3();
  const scl = new Vector3();
  const up = new Vector3(0, 1, 0);
  const normal = new Vector3();
  const tint = new Color();

  spots.forEach((p, i) => {
    const s = lerp(opts.scale[0], opts.scale[1], rng());
    const sy = opts.squash ? s * lerp(opts.squash[0], opts.squash[1], rng()) : s;
    pos.set(p.x, p.y - s * (opts.sink ?? 0), p.z);
    scl.set(s, sy, s);
    if (opts.alignToSlope) {
      // A moitie redressees : un objet strictement normal au sol a l'air de
      // glisser, un objet strictement vertical a l'air plante.
      terrainNormal(p.x, p.z, normal).lerp(up, 0.5).normalize();
      q.setFromUnitVectors(up, normal);
    } else {
      q.identity();
    }
    q.multiply(spin.setFromAxisAngle(up, rng() * Math.PI * 2));
    m.compose(pos, q, scl);
    mesh.setMatrixAt(i, m);

    if (opts.tint !== undefined) {
      const spread = opts.tintSpread ?? 0.16;
      tint.setHex(opts.tint).offsetHSL((rng() - 0.5) * 0.04, (rng() - 0.5) * 0.12, (rng() - 0.5) * spread);
      mesh.setColorAt(i, tint);
    }
  });

  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  scene.add(mesh);
  return mesh;
}

/**
 * Bornes de distance : un poteau tous les 50 m le long du champ de tir.
 *
 * Ce ne sont pas des decorations. Estimer une distance a l'oeil dans un
 * paysage sans echelle est a peu pres impossible ; les bornes donnent une
 * reference, et le HUD affiche la distance exacte du point visé. Les deux
 * ensemble permettent d'apprendre a « lire » une portee.
 */
function rangeMarkers(scene: Scene): void {
  const post = new CylinderGeometry(0.12, 0.14, 2.6, 7, 1).translate(0, 1.3, 0);
  const bands: BufferGeometry[] = [];
  const posts: BufferGeometry[] = [];

  for (let d = 50; d <= 250; d += 50) {
    const z = FIRING_LINE_Z + d;
    for (const x of [-128, 128]) {
      const y = terrainHeight(x, z);
      posts.push(post.clone().translate(x, y, z));
      // Une bande rouge par cinquantaine : 50 m = 1 bande, 250 m = 5 bandes.
      for (let b = 0; b < d / 50; b++) {
        bands.push(
          new BoxGeometry(0.34, 0.18, 0.34).translate(x, y + 0.5 + b * 0.32, z),
        );
      }
    }
  }

  scene.add(
    new Mesh(
      mergeGeometries(posts, false)!,
      new MeshStandardMaterial({ color: 0xd9d4c6, roughness: 0.85 }),
    ),
    new Mesh(
      mergeGeometries(bands, false)!,
      new MeshStandardMaterial({ color: 0xc0392b, roughness: 0.7 }),
    ),
  );
}

export function createScenery(scene: Scene): void {
  const rng = mulberry32(60271);

  place(
    scene,
    scatter(ROCK_COUNT, rng, { maxHeight: 128, maxSlope: 0.62 }),
    new IcosahedronGeometry(1, 0),
    new MeshStandardMaterial({ color: 0xffffff, roughness: 0.95, flatShading: true }),
    rng,
    { scale: [0.35, 1.9], squash: [0.45, 0.95], sink: 0.3, tint: 0x7e766a },
  );

  place(
    scene,
    scatter(SHRUB_COUNT, rng, { maxHeight: 70, floorBias: 0.8 }),
    shrubGeometry(),
    new MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.9,
      flatShading: true,
    }),
    rng,
    { scale: [0.3, 0.72], alignToSlope: true, sink: 0.18, tint: 0x7d7a3f, tintSpread: 0.2 },
  );

  // Blocs de beton : ce qui reste d'un parcours d'obstacles. Ils ne sont pas
  // repartis uniformement -- on les veut groupes, comme un depot.
  place(
    scene,
    scatter(BLOCK_COUNT, rng, { maxHeight: 34, maxSlope: 0.18 }),
    new BoxGeometry(1.9, 0.95, 0.95).translate(0, 0.42, 0),
    new MeshStandardMaterial({ color: 0xffffff, roughness: 0.92 }),
    rng,
    { scale: [0.8, 1.35], sink: 0.08, tint: 0x9d9a92, tintSpread: 0.1 },
  );

  place(
    scene,
    scatter(DRUM_COUNT, rng, { maxHeight: 40, maxSlope: 0.2 }),
    drumGeometry(),
    new MeshStandardMaterial({ color: 0xffffff, roughness: 0.55, metalness: 0.35 }),
    rng,
    { scale: [0.85, 1.1], tint: 0x8a5a2b, tintSpread: 0.22 },
  );

  rangeMarkers(scene);
}
